import React from "react";
// Styles
import styled, { keyframes } from "styled-components";
// State
import PropTypes from "prop-types";
// Icons
import { Icon } from "@iconify/react";
// Images
import { Light, Dark, Avatar } from "../config";
// Components
import { useErrorBoundary } from "react-error-boundary";
import { Link } from "react-scroll";
import { Button, Col, Container, Row } from "react-bootstrap";
import SocialLinks from "./SocialLinks";

// #region styled-components
const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const StyledHero = styled.header`
  position: relative;
  display: grid;
  place-items: center;
  max-width: 1920px;
  margin: 0 auto;
  min-height: calc(100vh - var(--nav-height));

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${({ theme }) =>
      theme.name === "light"
        ? `url(${Light}) center center/cover no-repeat`
        : `url(${Dark}) center center/cover no-repeat`};
    z-index: -2;
  }

  /* Overlay for contrast */
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${({ theme }) =>
      theme.name === "light"
        ? "rgba(255, 255, 255, 0.2)"
        : "rgba(0, 0, 0, 0.2)"};
    z-index: -1;
  }

  .down-container {
    height: 10rem;
  }

  @media (prefers-reduced-motion: no-preference) {
    .hero-img {
      animation: ${spin} infinite 20s linear;
    }
  }

  @media screen and (max-width: 767px) {
    &::before {
      background-size: auto 100vh;
    }
  }

  @media screen and (min-width: 768px) and (max-width: 1179px) {
    &::before {
      background-size: 100vw auto;
    }
  }

  @media screen and (min-width: 1180px) {
    &::before {
      background: ${({ theme }) =>
        theme.name === "light"
          ? `url(${Light}) top center fixed no-repeat`
          : `url(${Dark}) top center fixed no-repeat`};
      background-size: 100vw auto;
    }
  }

  @media screen and (min-width: 1367px) {
    &::before {
      background: ${({ theme }) =>
        theme.name === "light"
          ? `url(${Light}) center center fixed no-repeat`
          : `url(${Dark}) center center fixed no-repeat`};
      background-size: cover;
    }
  }
`;

const StyledRow = styled(Row)`
  margin-top: 5rem;
`;

// #endregion

// #region component
const propTypes = {
  name: PropTypes.string,
  subtitle: PropTypes.string,
};

const Hero = ({ name, subtitle }) => {
  const { showBoundary } = useErrorBoundary();

  return (
    <StyledHero>
      <Container>
        <StyledRow className="align-items-center text-center">
          <Col>
            <h1 className="mb-2 display-3 title">{name}</h1>
            {subtitle && (
              <p className="mb-3" style={{
                fontSize: '1.3rem',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                opacity: 0.85,
                fontWeight: 300,
              }}>{subtitle}</p>
            )}
            <div className="d-flex align-items-center justify-content-center">
              <SocialLinks />
            </div>
          </Col>
          <Col className="d-none d-md-block">
            <img
              src={Avatar}
              alt="Profile"
              className="rounded-circle"
              width={200}
              height={200}
              style={{ objectFit: 'cover' }}
            />
          </Col>
        </StyledRow>
        <Row className="align-items-end justify-content-center">
          <Col className="text-center">
            <Link to={"About"} className="link-icons">
              <Button variant="link" size="lg">
                <Icon icon="fa6-solid:circle-chevron-down" />
              </Button>
            </Link>
          </Col>
        </Row>
      </Container>
    </StyledHero>
  );
};

Hero.propTypes = propTypes;
// #endregion

export default Hero;
